/**
 * Session Layer - Fase 1 Core Engine
 * Agrupar interações por período ativo.
 * TTL padrão 3 h; nova sessão se TTL expirar, utm_source mudar ou domínio mudar (opcional).
 */

import type { CoreSession, UtmData } from '../types';
import { APP_NAME, SESSION_TTL_MS } from '../config';
import { generateShortSessionId } from '../utils/UrlSessionSync';

const SESSION_STORAGE_KEY = `${APP_NAME}_CORE_SESSION_V1`;

function generateSessionId(): string {
  return generateShortSessionId();
}

function parseUtmFromUrl(
  url: string = typeof window !== 'undefined' ? window.location.href : ''
): UtmData {
  const u = typeof window !== 'undefined' ? new URL(url) : null;
  if (!u) return {};
  const utm: UtmData = {};
  const keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];
  keys.forEach(k => {
    const v = u.searchParams.get(k);
    if (v) utm[k] = v;
  });
  return utm;
}

function getCurrentUtm(): UtmData {
  if (typeof window === 'undefined') return {};
  return parseUtmFromUrl(window.location.href);
}

function getCurrentOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

function loadStoredSession(): CoreSession | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as CoreSession;
    if (!s?.sessionId || !s?.visitorId || s.lastActivityAt == null) return null;
    return s;
  } catch {
    return null;
  }
}

function saveSession(session: CoreSession): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    }
  } catch {
    // ignore
  }
}

export interface GetOrCreateSessionOptions {
  visitorId: string;
  /** Nova sessão quando o domínio mudar (ex: redirect cross-domain). */
  newSessionOnDomainChange?: boolean;
  debug?: boolean;
}

/**
 * Verifica se a sessão ainda é válida (dentro do TTL e mesmo utm_source / domínio).
 */
function isSessionValid(
  session: CoreSession,
  options: GetOrCreateSessionOptions
): boolean {
  const now = Date.now();
  if (now - session.lastActivityAt > SESSION_TTL_MS) return false;

  const currentUtm = getCurrentUtm();
  if (
    currentUtm.utm_source !== undefined &&
    session.utmData?.utm_source !== undefined
  ) {
    if (currentUtm.utm_source !== session.utmData.utm_source) return false;
  }

  if (options.newSessionOnDomainChange) {
    const landingOrigin = new URL(session.landingPage).origin;
    if (landingOrigin !== getCurrentOrigin()) return false;
  }

  return true;
}

/**
 * Obtém ou cria a sessão atual.
 * Nova sessão se: TTL expirado, utm_source mudou ou (opcional) domínio mudou.
 * Exige visitorId: sessão é sempre vinculada a um visitor (nunca criar sessão sem visitor).
 */
export function getOrCreateSession(
  options: GetOrCreateSessionOptions
): CoreSession {
  const { visitorId, debug = false } = options;

  if (!visitorId || typeof visitorId !== 'string' || visitorId.trim() === '') {
    throw new Error(
      '[AgTracker] getOrCreateSession exige visitorId (vinculação visitor → session). Chame getOrCreateVisitor() antes.'
    );
  }

  const now = Date.now();
  const stored = loadStoredSession();

  if (
    stored &&
    stored.visitorId === visitorId &&
    isSessionValid(stored, options)
  ) {
    stored.lastActivityAt = now;
    saveSession(stored);
    if (debug)
      console.log(
        '[AgTracker Session] Sessão existente renovada:',
        stored.sessionId
      );
    return stored;
  }

  const utmData = getCurrentUtm();
  const session: CoreSession = {
    sessionId: generateSessionId(),
    visitorId,
    startedAt: now,
    lastActivityAt: now,
    landingPage: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    utmData,
    engagementScore: 0,
  };

  saveSession(session);
  if (debug) console.log('[AgTracker Session] Nova sessão:', session.sessionId);
  return session;
}

/**
 * Atualiza lastActivityAt e opcionalmente engagementScore (delta) da sessão atual.
 */
export function touchSession(
  session: CoreSession,
  engagementDelta: number = 0
): CoreSession {
  const updated = { ...session, lastActivityAt: Date.now() };
  if (engagementDelta)
    updated.engagementScore = (session.engagementScore || 0) + engagementDelta;
  saveSession(updated);
  return updated;
}

/**
 * Define o engagementScore absoluto da sessão (0–100).
 * Use em vez de touchSession(session, score) para evitar acúmulo errado.
 */
export function setSessionEngagementScore(
  session: CoreSession,
  score: number
): CoreSession {
  const updated = {
    ...session,
    lastActivityAt: Date.now(),
    engagementScore: Math.max(0, Math.min(100, score)),
  };
  saveSession(updated);
  return updated;
}

/**
 * Retorna a sessão armazenada sem validar TTL (para leitura).
 */
export function getStoredSession(): CoreSession | null {
  return loadStoredSession();
}
